import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Toast from '../../components/Common/Toast';
import ConfirmModal from '../../components/Common/ConfirmModal';
import PurchaseSiteForm from '../../components/Configuration/PurchaseSiteForm';
import { getPurchaseSites, deletePurchaseSite } from '../../services/purchaseSitesService';
import { useModuleSettings } from '../../contexts/ModuleSettingsContext';

const PurchaseSites = () => {
  const { comprasPurchaseSitesEnabled } = useModuleSettings();
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [siteToDelete, setSiteToDelete] = useState(null);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const hasLoaded = useRef(false);
  const [allSites, setAllSites] = useState([]);

  const loadSites = async () => {
    setLoading(true);
    try {
      const response = await getPurchaseSites();
      if (response.success) setAllSites(response.data || []);
    } catch (error) {
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al cargar sedes de compra',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!hasLoaded.current) {
      hasLoaded.current = true;
      loadSites();
    }
  }, []);

  const filtered = allSites.filter((site) => {
    const matchesSearch =
      !searchTerm ||
      (site.code || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (site.name || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active' && site.is_active) ||
      (statusFilter === 'inactive' && !site.is_active);
    return matchesSearch && matchesStatus;
  });

  const warehouseLabel = (site) => {
    const list = site.warehouses || [];
    if (!list.length) return '—';
    return list.map((w) => w.code || w.name).join(', ');
  };

  if (!comprasPurchaseSitesEnabled) {
    return (
      <div className="p-6">
        <p className="text-gray-600">
          Active &quot;Sedes de compra&quot; en{' '}
          <Link to="/administration/general-settings" className="text-teal-600 underline">
            Configuraciones generales
          </Link>
          .
        </p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6 flex flex-wrap justify-between items-start gap-4">
          <div>
            <Link to="/configuration" className="text-teal-600 hover:text-teal-800 text-sm mb-2 inline-block">
              ← Volver a configuración
            </Link>
            <h1 className="text-3xl font-bold text-gray-800">Sedes de compra</h1>
            <p className="text-gray-600 mt-1">Gestione sedes y sus almacenes asociados.</p>
          </div>
          <button
            type="button"
            onClick={() => {
              setEditingSite(null);
              setIsFormOpen(true);
            }}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
          >
            + Nueva sede
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 mb-6 flex flex-wrap gap-4">
          <input
            type="text"
            placeholder="Buscar..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 min-w-[200px] px-3 py-2 border border-gray-300 rounded-lg"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg"
          >
            <option value="all">Todos</option>
            <option value="active">Activos</option>
            <option value="inactive">Inactivos</option>
          </select>
        </div>

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Almacenes</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-500">Cargando…</td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-500">Sin sedes</td>
                </tr>
              ) : (
                filtered.map((site) => (
                  <tr key={site.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">{site.code}</td>
                    <td className="px-4 py-3 text-sm font-medium">{site.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{warehouseLabel(site)}</td>
                    <td className="px-4 py-3 text-sm">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs ${
                          site.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {site.is_active ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-right space-x-2">
                      <button
                        type="button"
                        className="text-teal-600 hover:underline"
                        onClick={() => {
                          setEditingSite(site);
                          setIsFormOpen(true);
                        }}
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        className="text-red-600 hover:underline"
                        onClick={() => {
                          setSiteToDelete(site);
                          setIsDeleteModalOpen(true);
                        }}
                      >
                        Eliminar
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <PurchaseSiteForm
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        purchaseSite={editingSite}
        onSuccess={() => {
          loadSites();
          setToast({ isOpen: true, message: 'Sede guardada', type: 'success' });
        }}
      />
      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={async () => {
          if (!siteToDelete) return;
          try {
            await deletePurchaseSite(siteToDelete.id);
            setToast({ isOpen: true, message: 'Sede eliminada', type: 'success' });
            loadSites();
          } catch (error) {
            setToast({
              isOpen: true,
              message: error.response?.data?.message || 'No se pudo eliminar',
              type: 'error',
            });
          } finally {
            setIsDeleteModalOpen(false);
            setSiteToDelete(null);
          }
        }}
        title="Eliminar sede"
        message={`¿Eliminar la sede ${siteToDelete?.name}?`}
      />
      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default PurchaseSites;
